import { useEffect, useMemo, useState } from 'react';
import { Logo } from '../components/Logo';
import { AdminSessionBanner } from '../components/AdminSessionBanner';
import { authedFetch } from '../lib/authed-fetch';
import { redirectToPortalLogin, useCurrentUser } from '../lib/use-current-user';
import '../admin.css';

/**
 * Journey QC — Prospects console (/admin/journey/prospects), Zach 11/2/26.
 *
 * A CRM-style view of every prospect in the Retirement Decision Journey:
 * find a person on the left, open their profile on the right and see the
 * whole picture in one place — the Decision Center link, the isolation
 * verdict (is anything on their DC somebody else's?), expected-vs-actual
 * for every sequence step, every email sent and scheduled (with the literal
 * delivered email), their meetings and the summaries THEY would see, the
 * message thread, the next 48 hours, and the audit trail — then stamp it.
 *
 * Backed by /api/admin/journey-prospects (list, prospect, audit-run) and
 * /api/admin/journey-audit (expected-vs-actual, stamps). Read-only except
 * the stamp.
 */

type ListRow = {
  profileId: string;
  name: string | null;
  email: string;
  advisor: string | null;
  isTest: boolean;
  enrollments: Array<{ enrollmentId: string; slug: string | null; status: string; enrolledAt: string }>;
  newestEnrolledAt: string;
  sent: number;
  pending: number;
  failed: number;
  nextSendAt: string | null;
  nextMeeting: { start: string; name: string } | null;
  meetingsHeld: number;
  summaries: number;
  messages: number;
  unanswered: number;
  hasPortalLink: boolean;
  portalLastSeenAt: string | null;
  lastAudit: { at: string; verdict: string; by: string } | null;
};

type ListPayload = {
  ok: boolean;
  error?: string;
  message?: string;
  includeTest: boolean;
  prospects: ListRow[];
  advisors: string[];
  sequences: string[];
  enrolledLast7d: number;
};

type Issue = { code: string; severity: 'critical' | 'warn' | 'info'; detail: string; surface?: string };

type Health = {
  ok: boolean;
  error?: string;
  message?: string;
  profileId: string;
  name: string | null;
  firstName: string | null;
  email: string;
  advisor: string | null;
  enrollments: Array<{ enrollmentId: string; sequence: string | null; status: string; isTest: boolean; enrolledAt: string; anchorBookingId: string | null }>;
  tokens: Array<{ tokenId: string; createdAt: string | null; lastSeenAt: string | null; revokedAt: string | null }>;
  portalLink: string | null;
  bookings: Array<{ id: string; start: string; status: string; name: string; slug: string | null; held: boolean | null }>;
  heldFacts: Array<{ bookingId: string; source: string; held: boolean; occurredAt: string | null; durationMinutes: number | null }>;
  sends: Array<{
    sendId: string;
    enrollmentId: string | null;
    templateKey: string;
    subject: string | null;
    status: string;
    skipReason: string | null;
    recipient: string | null;
    scheduledFor: string | null;
    sentAt: string | null;
    hasBody: boolean;
  }>;
  summaries: Array<{
    bookingId: string;
    meetingName: string;
    meetingStart: string | null;
    title: string | null;
    recordingStart: string | null;
    summary: string | null;
    inviteeEmails: string[];
    verified: boolean | null;
  }>;
  cohort: Array<{ arm: string; reason: string | null; amount: number | null; assignedAt: string | null }>;
  issues: Issue[];
  verdict: 'clean' | 'warn' | 'critical';
  audits: AuditRow[];
  auditTableMissing: boolean;
};

type StepView = {
  stepOrder: number | null;
  templateKey: string;
  channel: string;
  schedule: string;
  state: string;
  flag: string | null;
  note: string | null;
  send: { sendId: string; subject: string | null; scheduledFor: string | null; sentAt: string | null; skipReason: string | null; providerMessageId: string | null; hasBody: boolean } | null;
};

type AuditPayload = {
  ok: boolean;
  error?: string;
  message?: string;
  enrollments: Array<{ enrollmentId: string; sequence: string | null; sequenceStatus: string | null; status: string; isTest: boolean; compression: number; enrolledAt: string; steps: StepView[] }>;
  deliveryBySend: Record<string, Array<{ type: string; bounceType: string | null; at: string }>>;
  eventsNote: string | null;
  messages: Array<{ messageId: string; direction: string; author: string | null; body: string; at: string; repliedAt?: string }>;
  unanswered: number;
  next48: Array<{ at: string; kind: string; title: string; detail: string | null }>;
  anomalies: string[];
};

type AuditRow = {
  audit_id: string;
  enrollment_id: string | null;
  audited_by: string;
  audited_at: string;
  verdict: 'ok' | 'issue';
  note: string | null;
};

type RunReport = {
  ranAt: string;
  windowNote: string;
  prospects: number;
  enrollments: number;
  critical: number;
  warn: number;
  verdict: 'pass' | 'fail';
  globalIssues: Issue[];
  rows: Array<{ profileId: string; name: string | null; email: string; advisor: string | null; isTest: boolean; sequences: string[]; verdict: 'clean' | 'warn' | 'critical'; issues: Issue[] }>;
};

function fmtTs(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('en-US', { timeZone: 'America/New_York', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function ago(iso: string | null | undefined): string {
  if (!iso) return 'never';
  const ms = Date.now() - new Date(iso).getTime();
  const d = Math.floor(ms / 86_400_000);
  if (d >= 1) return `${d}d ago`;
  const h = Math.floor(ms / 3_600_000);
  if (h >= 1) return `${h}h ago`;
  return `${Math.max(1, Math.floor(ms / 60_000))}m ago`;
}

function stateClass(state: string): string {
  if (state === 'sent') return 'ok';
  if (state === 'failed' || state === 'missing') return 'bad';
  if (state === 'skipped' || state === 'cancelled') return 'muted';
  return 'wait';
}

const CSS = [
  '.ajp-wrap { display: grid; grid-template-columns: minmax(300px, 380px) minmax(0, 1fr); gap: 20px; align-items: start; }',
  '.ajp-wrap > div { min-width: 0; }',
  '.ajp-controls { display: flex; flex-wrap: wrap; gap: 8px; align-items: center; margin-top: 10px; }',
  '.ajp-controls select, .ajp-controls input[type=text], .ajp-controls input[type=search] { padding: 7px 9px; border: 1px solid #c4cbd4; border-radius: 6px; font: inherit; font-size: 13px; background: #fff; }',
  '.ajp-controls input[type=search] { min-width: 240px; }',
  '.ajp-controls label { font-size: 13px; color: #444; display: inline-flex; gap: 6px; align-items: center; }',
  '.ajp-pop { font-size: 12px; color: #6a6f6a; margin-left: 4px; }',
  '.ajp-list { max-height: calc(100vh - 230px); overflow-y: auto; padding-right: 4px; }',
  '.ajp-card { background: #fff; border: 1px solid #dfe3e8; border-radius: 8px; padding: 10px 12px; margin-bottom: 8px; cursor: pointer; }',
  '.ajp-card:hover { border-color: #0B1F3A; }',
  '.ajp-card.on { border-color: #C8102E; box-shadow: 0 0 0 1px #C8102E inset; }',
  '.ajp-card .nm { font-weight: 600; font-size: 14px; display: flex; justify-content: space-between; gap: 8px; align-items: baseline; }',
  '.ajp-card .nm small { font-weight: normal; font-size: 11.5px; color: #6a6f6a; white-space: nowrap; }',
  '.ajp-card .em { font-size: 12px; color: #6a6f6a; word-break: break-all; }',
  '.ajp-chips { display: flex; flex-wrap: wrap; gap: 5px; margin-top: 6px; }',
  '.ajp-chip { font-size: 11px; border: 1px solid #d6dbe2; border-radius: 10px; padding: 1px 8px; color: #444; background: #f6f8fa; white-space: nowrap; }',
  '.ajp-chip.test { border-color: #C8102E; color: #C8102E; background: #fff; }',
  '.ajp-chip.soon { border-color: #0B1F3A; color: #0B1F3A; }',
  '.ajp-chip.paused { border-color: #b4760a; color: #b4760a; background: #fff8e6; }',
  '.ajp-chip.ok { border-color: #1f7a3a; color: #1f7a3a; background: #eef8f0; }',
  '.ajp-chip.bad { border-color: #C8102E; color: #C8102E; background: #fff1f1; }',
  '.ajp-chip.warn { border-color: #e0b35a; color: #8a5a00; background: #fff8e6; }',
  '.ajp-detail { background: #fff; border: 1px solid #dfe3e8; border-radius: 8px; padding: 18px 20px; }',
  '.ajp-detail h2 { font-family: Georgia, serif; font-weight: normal; font-size: 24px; color: #0B1F3A; margin: 0 0 4px; }',
  '.ajp-detail h3 { font-size: 13px; letter-spacing: 0.6px; text-transform: uppercase; color: #6a6f6a; margin: 24px 0 8px; display: flex; align-items: baseline; gap: 10px; }',
  '.ajp-detail h3 .cnt { font-size: 11px; color: #8C8C8C; letter-spacing: 0; text-transform: none; }',
  '.ajp-meta { font-size: 13px; color: #6a6f6a; }',
  '.ajp-head { display: grid; grid-template-columns: repeat(auto-fit, minmax(190px, 1fr)); gap: 10px 18px; margin-top: 12px; font-size: 13px; }',
  '.ajp-head .k { font-size: 11px; letter-spacing: 0.5px; text-transform: uppercase; color: #8C8C8C; }',
  '.ajp-head .v { margin-top: 2px; word-break: break-word; }',
  '.ajp-link { background: none; border: none; padding: 0; color: #0B1F3A; text-decoration: underline; cursor: pointer; font: inherit; font-size: 12.5px; }',
  '.ajp-banner { border-radius: 8px; padding: 11px 14px; margin: 14px 0 0; font-size: 13.5px; }',
  '.ajp-banner.ok { background: #eef8f0; border: 1px solid #bfe3c8; color: #1f5a2d; }',
  '.ajp-banner.warn { background: #fff8e6; border: 1px solid #f0d9a0; color: #5f3f00; }',
  '.ajp-banner.bad { background: #fff1f1; border: 1px solid #f0b4b4; color: #8a1120; }',
  '.ajp-banner ul { margin: 6px 0 0; padding-left: 18px; }',
  '.ajp-banner li { margin: 3px 0; }',
  '.ajp-sev { display: inline-block; font-size: 10.5px; letter-spacing: 0.4px; text-transform: uppercase; border-radius: 9px; padding: 0 7px; margin-right: 6px; border: 1px solid; }',
  '.ajp-sev.critical { background: #fff1f1; border-color: #f0b4b4; color: #8a1120; }',
  '.ajp-sev.warn { background: #fff8e6; border-color: #f0d9a0; color: #8a5a00; }',
  '.ajp-sev.info { background: #f6f8fa; border-color: #d6dbe2; color: #444; }',
  '.ajp-enr { border: 1px solid #eef1f4; border-radius: 8px; padding: 10px 12px; margin-bottom: 10px; }',
  '.ajp-enr-head { display: flex; flex-wrap: wrap; gap: 8px; align-items: baseline; font-size: 13.5px; margin-bottom: 6px; }',
  '.ajp-enr-head strong { font-size: 14.5px; }',
  '.ajp-tw { overflow-x: auto; }',
  '.ajp-t { width: 100%; min-width: 560px; border-collapse: collapse; font-size: 13px; }',
  '.ajp-t th { text-align: left; font-size: 11px; letter-spacing: 0.5px; text-transform: uppercase; color: #6a6f6a; padding: 6px 8px; border-bottom: 1px solid #dfe3e8; }',
  '.ajp-t td { padding: 8px; border-bottom: 1px solid #eef1f4; vertical-align: top; }',
  '.ajp-t tr.up td { background: #fbfcfe; }',
  '.ajp-state { display: inline-block; font-size: 11px; border-radius: 10px; padding: 1px 8px; border: 1px solid #d6dbe2; white-space: nowrap; }',
  '.ajp-state.ok { background: #eef8f0; border-color: #bfe3c8; color: #1f5a2d; }',
  '.ajp-state.bad { background: #fff1f1; border-color: #f0b4b4; color: #8a1120; }',
  '.ajp-state.wait { background: #f6f8fa; color: #444; }',
  '.ajp-state.muted { background: #f3f3f3; color: #777; }',
  '.ajp-flag { color: #8a1120; font-size: 12.5px; margin-top: 3px; }',
  '.ajp-note { color: #6a6f6a; font-size: 12px; margin-top: 3px; }',
  '.ajp-mail { border: 1px solid #dfe3e8; border-radius: 6px; margin-top: 8px; background: #fff; }',
  '.ajp-mail iframe { width: 100%; height: 520px; border: 0; }',
  '.ajp-msg { border-left: 3px solid #c4cbd4; padding: 6px 10px; margin-bottom: 8px; font-size: 13px; background: #fafbfc; white-space: pre-wrap; }',
  '.ajp-msg.team { border-left-color: #C8102E; }',
  '.ajp-msg .who { font-size: 11.5px; color: #6a6f6a; margin-bottom: 2px; white-space: normal; }',
  '.ajp-msg .late { color: #8a1120; }',
  '.ajp-sum { border: 1px solid #eef1f4; border-radius: 8px; padding: 10px 12px; margin-bottom: 8px; font-size: 13px; }',
  '.ajp-sum .ttl { display: flex; flex-wrap: wrap; gap: 8px; align-items: baseline; }',
  '.ajp-sum pre { white-space: pre-wrap; font: inherit; margin: 8px 0 0; padding: 10px 12px; background: #f6f8fa; border-radius: 6px; max-height: 420px; overflow: auto; }',
  '.ajp-next { font-size: 13px; }',
  '.ajp-next li { margin-bottom: 4px; }',
  '.ajp-stamp { border-top: 1px solid #dfe3e8; margin-top: 18px; padding-top: 14px; }',
  '.ajp-stamp textarea { width: 100%; box-sizing: border-box; min-height: 70px; border: 1px solid #c4cbd4; border-radius: 6px; padding: 8px 10px; font: inherit; font-size: 13px; margin: 8px 0; }',
  '.ajp-stamp .row { display: flex; flex-wrap: wrap; gap: 14px; align-items: center; font-size: 13.5px; }',
  '.ajp-btn { background: #0B1F3A; color: #fff; border: none; border-radius: 4px; padding: 9px 16px; font: inherit; font-size: 13px; font-weight: 600; cursor: pointer; }',
  '.ajp-btn.ghost { background: #fff; color: #0B1F3A; border: 1px solid #0B1F3A; }',
  '.ajp-btn.sm { padding: 5px 10px; font-size: 12px; }',
  '.ajp-btn:disabled { opacity: 0.5; cursor: default; }',
  '.ajp-run { background: #fff; border: 1px solid #dfe3e8; border-left: 5px solid #0B1F3A; border-radius: 8px; padding: 12px 16px; margin-bottom: 18px; font-size: 13.5px; }',
  '.ajp-run.pass { border-left-color: #1f7a3a; }',
  '.ajp-run.fail { border-left-color: #C8102E; }',
  '.ajp-run .bar { display: flex; flex-wrap: wrap; gap: 10px 18px; align-items: center; }',
  '.ajp-run .ttl { font-weight: 700; font-size: 14px; }',
  '.ajp-run .spacer { flex: 1 1 auto; }',
  '.ajp-run table { border-collapse: collapse; font-size: 13px; margin-top: 10px; width: 100%; }',
  '.ajp-run td { padding: 5px 8px; border-top: 1px solid #eef1f4; vertical-align: top; }',
  '@media (max-width: 960px) { .ajp-wrap { grid-template-columns: 1fr; } .ajp-list { max-height: none; } }',
].join('\n');

export function AdminJourneyProspects() {
  const auth = useCurrentUser();
  const isAdmin = auth.status === 'authenticated' && auth.user.isAdmin;

  const [includeTest, setIncludeTest] = useState(true);
  const [list, setList] = useState<ListPayload | null>(null);
  const [listErr, setListErr] = useState<string | null>(null);
  const [loadingList, setLoadingList] = useState(false);
  const [search, setSearch] = useState('');
  const [advisor, setAdvisor] = useState('');
  const [sequence, setSequence] = useState('');
  const [status, setStatus] = useState<'all' | 'active' | 'paused_reply' | 'attention'>('all');

  const [selected, setSelected] = useState<string | null>(null);
  const [health, setHealth] = useState<Health | null>(null);
  const [audit, setAudit] = useState<AuditPayload | null>(null);
  const [detailErr, setDetailErr] = useState<string | null>(null);
  const [openSend, setOpenSend] = useState<string | null>(null);
  const [sendCache, setSendCache] = useState<Record<string, any>>({});
  const [openSummary, setOpenSummary] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const [verdict, setVerdict] = useState<'ok' | 'issue'>('ok');
  const [note, setNote] = useState('');
  const [stamping, setStamping] = useState(false);
  const [stampMsg, setStampMsg] = useState<string | null>(null);

  const [run, setRun] = useState<RunReport | null>(null);
  const [runBusy, setRunBusy] = useState(false);
  const [runMsg, setRunMsg] = useState<string | null>(null);
  const [runOpen, setRunOpen] = useState(false);

  useEffect(() => {
    if (auth.status === 'unauthenticated') window.location.href = redirectToPortalLogin();
  }, [auth.status]);

  async function loadList(test = includeTest) {
    setLoadingList(true);
    setListErr(null);
    try {
      const url = new URL('/api/admin/journey-prospects', window.location.origin);
      url.searchParams.set('view', 'list');
      if (test) url.searchParams.set('test', '1');
      const res = await authedFetch(url.toString(), { cache: 'no-store' });
      const json = (await res.json()) as ListPayload;
      if (!json.ok) throw new Error(json.message ?? json.error ?? 'Request failed');
      setList(json);
    } catch (e) {
      setListErr(e instanceof Error ? e.message : 'Network error');
    } finally {
      setLoadingList(false);
    }
  }

  async function openProspect(profileId: string) {
    setSelected(profileId);
    setHealth(null);
    setAudit(null);
    setDetailErr(null);
    setOpenSend(null);
    setOpenSummary(null);
    setStampMsg(null);
    setNote('');
    setVerdict('ok');
    setCopied(false);
    try {
      const u1 = new URL('/api/admin/journey-prospects', window.location.origin);
      u1.searchParams.set('view', 'prospect');
      u1.searchParams.set('profile', profileId);
      const u2 = new URL('/api/admin/journey-audit', window.location.origin);
      u2.searchParams.set('view', 'prospect');
      u2.searchParams.set('profile', profileId);
      const [r1, r2] = await Promise.all([authedFetch(u1.toString(), { cache: 'no-store' }), authedFetch(u2.toString(), { cache: 'no-store' })]);
      const h = (await r1.json()) as Health;
      if (!h.ok) throw new Error(h.message ?? h.error ?? 'Request failed');
      setHealth(h);
      const a = (await r2.json()) as AuditPayload;
      if (a.ok) setAudit(a);
      if (h.verdict === 'critical') setVerdict('issue');
      const url = new URL(window.location.href);
      url.searchParams.set('profile', profileId);
      window.history.replaceState(null, '', url.toString());
    } catch (e) {
      setDetailErr(e instanceof Error ? e.message : 'Network error');
    }
  }

  async function toggleSend(sendId: string) {
    if (openSend === sendId) {
      setOpenSend(null);
      return;
    }
    setOpenSend(sendId);
    if (sendCache[sendId]) return;
    try {
      const url = new URL('/api/admin/journey-qc', window.location.origin);
      url.searchParams.set('send', sendId);
      const res = await authedFetch(url.toString(), { cache: 'no-store' });
      const json = await res.json();
      setSendCache((c) => ({ ...c, [sendId]: json.ok ? json.send : { error: json.error ?? 'not found' } }));
    } catch {
      setSendCache((c) => ({ ...c, [sendId]: { error: 'Network error' } }));
    }
  }

  async function stamp() {
    if (!health) return;
    setStamping(true);
    setStampMsg(null);
    try {
      const snapshot = {
        verdict: health.verdict,
        issues: health.issues,
        anomalies: audit?.anomalies ?? [],
        enrollments: (audit?.enrollments ?? []).map((e) => ({ sequence: e.sequence, status: e.status, steps: e.steps.map((s) => ({ templateKey: s.templateKey, state: s.state, flag: s.flag })) })),
      };
      const res = await authedFetch('/api/admin/journey-audit', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ profileId: health.profileId, enrollmentId: health.enrollments[0]?.enrollmentId ?? null, verdict, note: note.trim() || null, snapshot }),
      });
      const json = await res.json();
      if (!json.ok) {
        if (json.error === 'audit_table_missing') throw new Error('The audit table is not there yet (migration 0097). The verification was not saved.');
        throw new Error(json.message ?? json.error ?? 'Request failed');
      }
      setHealth({ ...health, audits: [json.audit, ...health.audits] });
      setStampMsg(`Recorded as ${verdict === 'ok' ? 'verified' : 'issue'} at ${fmtTs(json.audit.audited_at)}.`);
      setNote('');
      setList((l) => (l ? { ...l, prospects: l.prospects.map((r) => (r.profileId === health.profileId ? { ...r, lastAudit: { at: json.audit.audited_at, verdict, by: json.audit.audited_by } } : r)) } : l));
    } catch (e) {
      setStampMsg(e instanceof Error ? e.message : 'Network error');
    } finally {
      setStamping(false);
    }
  }

  async function runAudit(notify: boolean) {
    if (notify && !window.confirm('Run the full audit now and email the report (and Slack, if wired)?')) return;
    setRunBusy(true);
    setRunMsg(null);
    try {
      const url = new URL('/api/admin/journey-prospects', window.location.origin);
      url.searchParams.set('view', 'audit-run');
      if (includeTest) url.searchParams.set('test', '1');
      if (notify) url.searchParams.set('notify', '1');
      const res = await authedFetch(url.toString(), { cache: 'no-store' });
      const json = await res.json();
      if (!json.ok) throw new Error(json.message ?? json.error ?? 'Request failed');
      setRun(json.report as RunReport);
      setRunOpen(true);
      if (json.delivery) setRunMsg(`Report email ${json.delivery.email}${json.delivery.emailDetail ? ` (${json.delivery.emailDetail})` : ''} · Slack ${json.delivery.slack}.`);
    } catch (e) {
      setRunMsg(e instanceof Error ? e.message : 'Network error');
    } finally {
      setRunBusy(false);
    }
  }

  useEffect(() => {
    if (!isAdmin) return;
    void loadList(true);
    const pre = new URLSearchParams(window.location.search).get('profile');
    if (pre) void openProspect(pre);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAdmin]);

  const filtered = useMemo(() => {
    if (!list) return [];
    const q = search.trim().toLowerCase();
    return list.prospects.filter((r) => {
      if (advisor && (r.advisor ?? '').toLowerCase() !== advisor.toLowerCase()) return false;
      if (sequence && !r.enrollments.some((e) => e.slug === sequence)) return false;
      if (status === 'active' && !r.enrollments.some((e) => e.status === 'active')) return false;
      if (status === 'paused_reply' && !r.enrollments.some((e) => e.status === 'paused_reply')) return false;
      if (status === 'attention' && !(r.failed > 0 || r.unanswered > 0 || r.enrollments.some((e) => e.status === 'paused_reply') || r.lastAudit?.verdict === 'issue')) return false;
      if (q) {
        const hay = `${r.name ?? ''} ${r.email} ${r.advisor ?? ''}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [list, search, advisor, sequence, status]);

  const verdictOf = (p: ListRow): { cls: string; label: string } | null => {
    const r = run?.rows.find((x) => x.profileId === p.profileId);
    if (!r) return null;
    return r.verdict === 'clean' ? { cls: 'ok', label: 'clean' } : r.verdict === 'warn' ? { cls: 'warn', label: 'warnings' } : { cls: 'bad', label: 'critical' };
  };

  function renderRun() {
    return (
      <div className={'ajp-run ' + (run ? run.verdict : '')}>
        <div className="bar">
          <span className="ttl">Full audit</span>
          {run ? (
            <span className="ajp-meta">
              last run {fmtTs(run.ranAt)} · <strong>{run.verdict === 'pass' ? 'passed' : 'FAILED'}</strong> · {run.prospects} prospects · {run.critical} critical · {run.warn} warnings
              {' · '}
              <button type="button" className="ajp-link" onClick={() => setRunOpen((o) => !o)}>
                {runOpen ? 'hide' : 'show'} details
              </button>
            </span>
          ) : (
            <span className="ajp-meta">Every Saturday morning the same audit runs on its own and emails the result. Run it now to check everyone immediately.</span>
          )}
          <span className="spacer" />
          <button type="button" className="ajp-btn ghost sm" disabled={runBusy || !isAdmin} onClick={() => void runAudit(false)}>
            {runBusy ? 'Running…' : 'Run audit now'}
          </button>
          <button type="button" className="ajp-btn sm" disabled={runBusy || !isAdmin} onClick={() => void runAudit(true)}>
            Run + email me
          </button>
        </div>
        {runMsg && <p className="ajp-meta" style={{ margin: '8px 0 0' }}>{runMsg}</p>}
        {run && runOpen && (
          <>
            {run.globalIssues.length > 0 && (
              <ul style={{ margin: '8px 0 0', paddingLeft: 18, color: '#8a1120' }}>
                {run.globalIssues.map((g, i) => (
                  <li key={i}>
                    <span className={'ajp-sev ' + g.severity}>{g.severity}</span>
                    {g.detail}
                  </li>
                ))}
              </ul>
            )}
            {run.rows.filter((r) => r.verdict !== 'clean').length === 0 ? (
              <p className="ajp-meta" style={{ margin: '8px 0 0' }}>Nothing flagged. {run.windowNote}.</p>
            ) : (
              <table>
                <tbody>
                  {run.rows
                    .filter((r) => r.verdict !== 'clean')
                    .map((r) => (
                      <tr key={r.profileId}>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <button type="button" className="ajp-link" onClick={() => void openProspect(r.profileId)}>
                            {r.name ?? r.email}
                          </button>
                          {r.isTest && <span className="ajp-chip test" style={{ marginLeft: 6 }}>test</span>}
                        </td>
                        <td>
                          {r.issues.map((i, k) => (
                            <div key={k}>
                              <span className={'ajp-sev ' + i.severity}>{i.severity}</span>
                              <code style={{ fontSize: 12 }}>{i.code}</code> — {i.detail}
                            </div>
                          ))}
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            )}
          </>
        )}
      </div>
    );
  }

  function renderIssues(h: Health) {
    if (h.verdict === 'clean')
      return (
        <div className="ajp-banner ok">
          <strong>Isolation verified.</strong> Their portal token resolves to this profile only; every booking, meeting summary, Decision Center message and journey email on their account traces back to {h.email}. Nothing is overdue, failed or waiting on a reply.
        </div>
      );
    return (
      <div className={'ajp-banner ' + (h.verdict === 'critical' ? 'bad' : 'warn')}>
        <strong>
          {h.verdict === 'critical' ? 'Needs attention now' : 'Worth a look'} · {h.issues.length} item{h.issues.length === 1 ? '' : 's'}
        </strong>
        <ul>
          {h.issues.map((i, k) => (
            <li key={k}>
              <span className={'ajp-sev ' + i.severity}>{i.severity}</span>
              <code style={{ fontSize: 12 }}>{i.code}</code> — {i.detail}
              {i.surface ? <span className="ajp-note"> ({i.surface})</span> : null}
            </li>
          ))}
        </ul>
      </div>
    );
  }

  function renderSteps(a: AuditPayload) {
    return a.enrollments.map((e) => (
      <div className="ajp-enr" key={e.enrollmentId}>
        <div className="ajp-enr-head">
          <strong>{e.sequence ?? 'sequence'}</strong>
          <span className={'ajp-chip ' + (e.status === 'active' ? 'ok' : e.status === 'paused_reply' ? 'paused' : '')}>{e.status}</span>
          {e.isTest && <span className="ajp-chip test">test ×{e.compression}</span>}
          <span className="ajp-meta">enrolled {fmtTs(e.enrolledAt)}</span>
          {e.sequenceStatus && e.sequenceStatus !== 'live' && <span className="ajp-chip">sequence {e.sequenceStatus}</span>}
        </div>
        <div className="ajp-tw">
          <table className="ajp-t">
            <thead>
              <tr>
                <th>Step</th>
                <th>Planned</th>
                <th>Actual</th>
                <th>When</th>
              </tr>
            </thead>
            <tbody>
              {e.steps.map((s, i) => {
                const deliv = s.send ? a.deliveryBySend[s.send.sendId] ?? a.deliveryBySend[s.send.providerMessageId ?? ''] ?? [] : [];
                return (
                  <tr key={s.templateKey + i}>
                    <td>
                      <div>{s.templateKey}</div>
                      {s.send?.subject && <div className="ajp-note">{s.send.subject}</div>}
                    </td>
                    <td>{s.schedule}</td>
                    <td>
                      <span className={'ajp-state ' + stateClass(s.state)}>{s.state}</span>
                      {s.send?.skipReason && <div className="ajp-note">{s.send.skipReason}</div>}
                      {s.flag && <div className="ajp-flag">{s.flag}</div>}
                      {s.note && <div className="ajp-note">{s.note}</div>}
                      {deliv.length > 0 && (
                        <div className="ajp-note">
                          {deliv.map((d, j) => (
                            <span key={j}>
                              {j ? ' · ' : ''}
                              {d.type}
                              {d.bounceType ? ` (${d.bounceType})` : ''} {fmtTs(d.at)}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td>
                      {s.send?.sentAt ? (
                        <>
                          sent {fmtTs(s.send.sentAt)}
                          {s.send.scheduledFor && <div className="ajp-note">planned {fmtTs(s.send.scheduledFor)}</div>}
                        </>
                      ) : s.send?.scheduledFor ? (
                        <>planned {fmtTs(s.send.scheduledFor)}</>
                      ) : (
                        '—'
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    ));
  }

  function renderEmails(h: Health) {
    const now = Date.now();
    const sends = [...h.sends].sort((x, y) => (x.scheduledFor ?? x.sentAt ?? '').localeCompare(y.scheduledFor ?? y.sentAt ?? ''));
    if (!sends.length) return <p className="admin-empty">No journey emails materialized yet.</p>;
    return (
      <div className="ajp-tw">
        <table className="ajp-t">
          <thead>
            <tr>
              <th>Email</th>
              <th>To</th>
              <th>Status</th>
              <th>When</th>
            </tr>
          </thead>
          <tbody>
            {sends.map((s) => {
              const upcoming = s.status === 'pending' && s.scheduledFor && new Date(s.scheduledFor).getTime() > now;
              const cached = sendCache[s.sendId];
              const wrongTo = s.recipient && h.email && s.recipient.toLowerCase() !== h.email;
              return (
                <tr key={s.sendId} className={upcoming ? 'up' : ''}>
                  <td>
                    <div>{s.templateKey}</div>
                    {s.subject && <div className="ajp-note">{s.subject}</div>}
                    {s.hasBody && (
                      <div>
                        <button type="button" className="ajp-link" onClick={() => void toggleSend(s.sendId)}>
                          {openSend === s.sendId ? 'hide email' : 'view email as delivered'}
                        </button>
                      </div>
                    )}
                    {openSend === s.sendId && (
                      <div className="ajp-mail">
                        {!cached && <p className="admin-empty">Loading…</p>}
                        {cached?.error && <p className="admin-empty">{cached.error}</p>}
                        {cached?.body_html && <iframe title="delivered email" sandbox="" srcDoc={cached.body_html} />}
                        {cached && !cached.body_html && cached.body_text && <pre style={{ whiteSpace: 'pre-wrap', padding: 12, margin: 0, fontSize: 13 }}>{cached.body_text}</pre>}
                      </div>
                    )}
                  </td>
                  <td>
                    {s.recipient ?? <span className="ajp-note">{h.email}</span>}
                    {wrongTo && <div className="ajp-flag">not this prospect's address</div>}
                  </td>
                  <td>
                    <span className={'ajp-state ' + (upcoming ? 'wait' : stateClass(s.status))}>{upcoming ? 'scheduled' : s.status}</span>
                    {s.skipReason && <div className="ajp-note">{s.skipReason}</div>}
                  </td>
                  <td>
                    {s.sentAt ? `sent ${fmtTs(s.sentAt)}` : s.scheduledFor ? `${upcoming ? 'goes out' : 'planned'} ${fmtTs(s.scheduledFor)}` : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  function renderMeetings(h: Health) {
    if (!h.bookings.length) return <p className="admin-empty">No bookings on this email.</p>;
    return (
      <div className="ajp-tw">
        <table className="ajp-t" style={{ minWidth: 420 }}>
          <thead>
            <tr>
              <th>Meeting</th>
              <th>When</th>
              <th>Status</th>
              <th>Held</th>
              <th>Summary</th>
            </tr>
          </thead>
          <tbody>
            {h.bookings.map((b) => {
              const facts = h.heldFacts.filter((f) => f.bookingId === b.id);
              const sums = h.summaries.filter((s) => s.bookingId === b.id);
              return (
                <tr key={b.id}>
                  <td>{b.name}</td>
                  <td>{fmtTs(b.start)}</td>
                  <td>
                    <span className={'ajp-state ' + (b.status === 'confirmed' ? 'ok' : b.status === 'cancelled' || b.status === 'no_show' ? 'muted' : 'wait')}>{b.status}</span>
                  </td>
                  <td>
                    {b.held === null ? <span className="ajp-note">no fact yet</span> : b.held ? 'yes' : 'no'}
                    {facts.length > 0 && <div className="ajp-note">{facts.map((f) => `${f.source}${f.held ? '' : ' (not held)'}${f.durationMinutes != null ? ` ${f.durationMinutes}m` : ''}`).join(' · ')}</div>}
                  </td>
                  <td>{sums.length ? `${sums.length} matched` : <span className="ajp-note">none</span>}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  function renderSummaries(h: Health) {
    if (!h.summaries.length) return <p className="admin-empty">No meeting summaries would show in their Decision Center yet.</p>;
    return h.summaries.map((s, i) => {
      const key = s.bookingId + ':' + i;
      return (
        <div className="ajp-sum" key={key}>
          <div className="ttl">
            <strong>{s.title ?? s.meetingName}</strong>
            <span className="ajp-meta">
              {s.meetingName} · {fmtTs(s.meetingStart)}
              {s.recordingStart ? ` · recorded ${fmtTs(s.recordingStart)}` : ''}
            </span>
            {s.verified === true && <span className="ajp-chip ok">invitee list names this prospect</span>}
            {s.verified === false && <span className="ajp-chip bad">invitees do NOT include this prospect</span>}
            {s.verified === null && <span className="ajp-chip warn">no invitee list to verify</span>}
            <button type="button" className="ajp-link" onClick={() => setOpenSummary(openSummary === key ? null : key)}>
              {openSummary === key ? 'hide summary' : 'read summary as the prospect sees it'}
            </button>
          </div>
          {s.inviteeEmails.length > 0 && <div className="ajp-note">invitees: {s.inviteeEmails.join(', ')}</div>}
          {openSummary === key && <pre>{s.summary}</pre>}
        </div>
      );
    });
  }

  function renderDetail() {
    if (detailErr)
      return (
        <div className="admin-err" role="alert">
          <strong>Error:</strong> {detailErr}
        </div>
      );
    if (!selected) return <p className="admin-empty">Pick a prospect on the left — or search for one — to open their profile.</p>;
    if (!health) return <p className="admin-empty">Loading…</p>;
    const h = health;
    const liveTok = h.tokens.find((t) => !t.revokedAt) ?? null;
    const nextMeeting = h.bookings.find((b) => b.status === 'confirmed' && new Date(b.start).getTime() > Date.now()) ?? null;
    const upcomingSends = h.sends.filter((s) => s.status === 'pending').length;
    return (
      <div className="ajp-detail">
        <h2>{h.name ?? h.email}</h2>
        <div className="ajp-meta">
          {h.email}
          {h.advisor ? ` · advisor: ${h.advisor}` : ' · no owner advisor'}
          {h.enrollments.some((e) => e.isTest) ? ' · test profile' : ''}
          {' · '}
          <a href={'/admin/journey?email=' + encodeURIComponent(h.email)} target="_blank" rel="noopener noreferrer">
            raw timeline ↗
          </a>
        </div>

        <div className="ajp-head">
          <div>
            <div className="k">Decision Center</div>
            <div className="v">
              {h.portalLink ? (
                <>
                  <a href={h.portalLink} target="_blank" rel="noopener noreferrer">
                    open as the prospect ↗
                  </a>
                  {' · '}
                  <button
                    type="button"
                    className="ajp-link"
                    onClick={() => {
                      void navigator.clipboard?.writeText(h.portalLink!).then(() => setCopied(true));
                    }}
                  >
                    {copied ? 'copied' : 'copy link'}
                  </button>
                  <div className="ajp-note">
                    {liveTok?.lastSeenAt ? `last opened ${fmtTs(liveTok.lastSeenAt)} (${ago(liveTok.lastSeenAt)})` : 'never opened'}
                    {liveTok?.createdAt ? ` · link minted ${fmtTs(liveTok.createdAt)}` : ''}
                  </div>
                </>
              ) : (
                <span className="ajp-note">no live portal link</span>
              )}
            </div>
          </div>
          <div>
            <div className="k">Journey</div>
            <div className="v">
              {h.enrollments.length === 0 && <span className="ajp-note">not enrolled</span>}
              {h.enrollments.map((e) => (
                <div key={e.enrollmentId}>
                  {e.sequence ?? 'sequence'} <span className={'ajp-chip ' + (e.status === 'active' ? 'ok' : e.status === 'paused_reply' ? 'paused' : '')}>{e.status}</span>{' '}
                  <span className="ajp-note">since {fmtTs(e.enrolledAt)}</span>
                </div>
              ))}
            </div>
          </div>
          <div>
            <div className="k">Meetings</div>
            <div className="v">
              {nextMeeting ? `next: ${nextMeeting.name} ${fmtTs(nextMeeting.start)}` : 'no next meeting booked'}
              <div className="ajp-note">
                {h.bookings.filter((b) => b.held).length} held · {h.summaries.length} summar{h.summaries.length === 1 ? 'y' : 'ies'} · {h.sends.filter((s) => s.status === 'sent').length} emails sent · {upcomingSends} scheduled
              </div>
            </div>
          </div>
          <div>
            <div className="k">Cohort</div>
            <div className="v">
              {h.cohort.length ? (
                <>
                  {h.cohort[0].arm}
                  {h.cohort[0].reason ? ` (${h.cohort[0].reason})` : ''}
                  {h.cohort[0].amount != null ? ` · $${Math.round(h.cohort[0].amount).toLocaleString()}` : ''}
                  <div className="ajp-note">assigned {fmtTs(h.cohort[0].assignedAt)}</div>
                </>
              ) : (
                <span className="ajp-note">not in the cohort ledger (pre-gate or test)</span>
              )}
            </div>
          </div>
        </div>

        {renderIssues(h)}
        {audit && audit.anomalies.length > 0 && (
          <div className="ajp-banner warn" style={{ marginTop: 8 }}>
            <strong>Timing notes from expected-vs-actual</strong>
            <ul>
              {audit.anomalies.map((a, i) => (
                <li key={i}>{a}</li>
              ))}
            </ul>
          </div>
        )}

        <h3>
          Expected vs actual <span className="cnt">per sequence step</span>
        </h3>
        {!audit && <p className="admin-empty">Loading the step plan…</p>}
        {audit && audit.enrollments.length === 0 && <p className="admin-empty">No journey enrollments for this prospect.</p>}
        {audit && renderSteps(audit)}
        {audit?.eventsNote && <p className="ajp-note">{audit.eventsNote}</p>}

        <h3>
          Emails <span className="cnt">everything sent and everything scheduled</span>
        </h3>
        {renderEmails(h)}

        <h3>
          Meetings <span className="cnt">what their Your meetings page is built from</span>
        </h3>
        {renderMeetings(h)}

        <h3>
          Meeting summaries <span className="cnt">exactly the ones their Decision Center would show</span>
        </h3>
        {renderSummaries(h)}

        <h3>
          Messages {audit?.unanswered ? <span className="cnt" style={{ color: '#8a1120' }}>· {audit.unanswered} unanswered</span> : null}
        </h3>
        {!audit && <p className="admin-empty">Loading…</p>}
        {audit && audit.messages.length === 0 && <p className="admin-empty">No Decision Center messages.</p>}
        {audit?.messages.map((m) => {
          const waitH = m.direction === 'prospect' ? ((m.repliedAt ? new Date(m.repliedAt).getTime() : Date.now()) - new Date(m.at).getTime()) / 3_600_000 : 0;
          return (
            <div className={'ajp-msg ' + (m.direction === 'team' ? 'team' : '')} key={m.messageId}>
              <div className="who">
                {m.direction === 'team' ? `Reply from ${m.author ?? 'the team'}` : `${h.name ?? 'Prospect'}`} · {fmtTs(m.at)}
                {m.direction === 'prospect' && (
                  <span className={waitH > 24 ? ' late' : ''}>
                    {' · '}
                    {m.repliedAt ? `answered in ${Math.round(waitH * 10) / 10}h` : `unanswered for ${Math.round(waitH * 10) / 10}h`}
                  </span>
                )}
              </div>
              {m.body}
            </div>
          );
        })}

        <h3>Next 48 hours</h3>
        {!audit && <p className="admin-empty">Loading…</p>}
        {audit && audit.next48.length === 0 && <p className="admin-empty">Nothing scheduled in the next 48 hours.</p>}
        {audit && audit.next48.length > 0 && (
          <ul className="ajp-next">
            {audit.next48.map((x, i) => (
              <li key={i}>
                <strong>{fmtTs(x.at)}</strong> · {x.kind === 'send' ? 'send' : 'meeting'}: {x.title}
                {x.detail ? <span className="ajp-note"> · {x.detail}</span> : null}
              </li>
            ))}
          </ul>
        )}

        <div className="ajp-stamp">
          <h3 style={{ marginTop: 0 }}>Audit</h3>
          {h.audits.length > 0 && (
            <p className="ajp-meta">
              {h.audits
                .slice(0, 6)
                .map((a) => `${a.verdict === 'ok' ? 'verified' : 'issue'} by ${a.audited_by} ${fmtTs(a.audited_at)}${a.note ? ` — ${a.note.slice(0, 80)}` : ''}`)
                .join(' · ')}
            </p>
          )}
          {h.auditTableMissing && <p className="ajp-flag">Audit table not found (migration 0097 not run yet). Verifications will not save until it is.</p>}
          <div className="row">
            <label>
              <input type="radio" name="verdict" checked={verdict === 'ok'} onChange={() => setVerdict('ok')} /> Verified, working as intended
            </label>
            <label>
              <input type="radio" name="verdict" checked={verdict === 'issue'} onChange={() => setVerdict('issue')} /> Issue found
            </label>
          </div>
          <textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder="What you checked, what you saw (optional for a clean pass; say what's wrong for an issue)." />
          <div className="row">
            <button type="button" className="ajp-btn" disabled={stamping} onClick={() => void stamp()}>
              {stamping ? 'Saving…' : verdict === 'ok' ? 'Mark verified' : 'Record issue'}
            </button>
            {stampMsg && <span className="ajp-meta">{stampMsg}</span>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-app">
      <style>{CSS}</style>
      <AdminSessionBanner />
      <header className="admin-header">
        <div className="admin-header-inner">
          <Logo className="admin-logo" />
          <div>
            <h1>Journey QC · Prospects</h1>
            <p className="admin-sub">Every prospect in the Retirement Decision Journey. Open a profile to see their Decision Center, every email, their meetings and summaries, the thread — and whether any of it belongs to someone else.</p>
          </div>
        </div>
        <div className="admin-filters">
          <div className="ajp-controls">
            <input type="search" placeholder="Search name, email or advisor" value={search} onChange={(e) => setSearch(e.target.value)} />
            <label>
              Advisor
              <select value={advisor} onChange={(e) => setAdvisor(e.target.value)}>
                <option value="">any</option>
                {(list?.advisors ?? []).map((a) => (
                  <option key={a} value={a}>
                    {a}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Sequence
              <select value={sequence} onChange={(e) => setSequence(e.target.value)}>
                <option value="">any</option>
                {(list?.sequences ?? []).map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
            <label>
              Show
              <select value={status} onChange={(e) => setStatus(e.target.value as any)}>
                <option value="all">everyone</option>
                <option value="active">active journeys</option>
                <option value="paused_reply">paused on a reply</option>
                <option value="attention">needs attention</option>
              </select>
            </label>
            <label>
              <input
                type="checkbox"
                checked={includeTest}
                onChange={(e) => {
                  setIncludeTest(e.target.checked);
                  void loadList(e.target.checked);
                }}
              />{' '}
              include test profiles
            </label>
            <button type="button" className="ajp-btn ghost sm" disabled={loadingList || !isAdmin} onClick={() => void loadList()}>
              {loadingList ? 'Refreshing…' : 'Refresh'}
            </button>
            {list && (
              <span className="ajp-pop">
                {filtered.length} of {list.prospects.length} prospect{list.prospects.length === 1 ? '' : 's'} · {list.enrolledLast7d} enrolled this week
              </span>
            )}
          </div>
        </div>
        <nav className="admin-nav">
          <a href="/admin" className="admin-nav-link">
            Admin home
          </a>
          <a href="/admin/journey/audit" className="admin-nav-link">
            Random audit + launch gate
          </a>
          <a href="/admin/journey" className="admin-nav-link">
            Raw QC console
          </a>
        </nav>
      </header>

      <main className="admin-main">
        {auth.status === 'loading' && <p className="admin-empty">Checking session…</p>}
        {auth.status === 'authenticated' && !auth.user.isAdmin && (
          <div className="admin-err" role="alert">
            Admin access required.
          </div>
        )}
        {isAdmin && (
          <>
            {renderRun()}
            {listErr && (
              <div className="admin-err" role="alert">
                <strong>Error:</strong> {listErr}
              </div>
            )}
            <div className="ajp-wrap">
              <div className="ajp-list">
                {!list && !listErr && <p className="admin-empty">Loading prospects…</p>}
                {list && filtered.length === 0 && <p className="admin-empty">No prospects match. Clear the search or widen the filters.</p>}
                {filtered.map((r) => {
                  const v = verdictOf(r);
                  const paused = r.enrollments.some((e) => e.status === 'paused_reply');
                  const active = r.enrollments.some((e) => e.status === 'active');
                  return (
                    <div
                      key={r.profileId}
                      className={'ajp-card ' + (selected === r.profileId ? 'on' : '')}
                      role="button"
                      tabIndex={0}
                      onClick={() => void openProspect(r.profileId)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          void openProspect(r.profileId);
                        }
                      }}
                    >
                      <div className="nm">
                        <span>{r.name ?? r.email}</span>
                        <small>enrolled {ago(r.newestEnrolledAt)}</small>
                      </div>
                      <div className="em">
                        {r.email}
                        {r.advisor ? ` · ${r.advisor}` : ''}
                      </div>
                      <div className="ajp-chips">
                        {r.enrollments.slice(0, 3).map((e) => (
                          <span key={e.enrollmentId} className={'ajp-chip ' + (e.status === 'paused_reply' ? 'paused' : e.status === 'active' ? 'ok' : '')}>
                            {e.slug ?? 'sequence'} · {e.status}
                          </span>
                        ))}
                        {r.isTest && <span className="ajp-chip test">test</span>}
                        {v && <span className={'ajp-chip ' + v.cls}>audit: {v.label}</span>}
                        {r.failed > 0 && <span className="ajp-chip bad">{r.failed} failed</span>}
                        {r.unanswered > 0 && <span className="ajp-chip bad">awaiting reply</span>}
                        {r.nextMeeting && new Date(r.nextMeeting.start).getTime() - Date.now() < 7 * 86_400_000 && <span className="ajp-chip soon">meeting {fmtTs(r.nextMeeting.start)}</span>}
                        <span className="ajp-chip">
                          {r.sent} sent · {r.pending} scheduled
                        </span>
                        {r.summaries > 0 && <span className="ajp-chip">{r.summaries} summar{r.summaries === 1 ? 'y' : 'ies'}</span>}
                        {r.messages > 0 && <span className="ajp-chip">{r.messages} msg</span>}
                        {!r.hasPortalLink && active && <span className="ajp-chip warn">no DC link</span>}
                        {r.hasPortalLink && <span className="ajp-chip">{r.portalLastSeenAt ? `DC opened ${ago(r.portalLastSeenAt)}` : 'DC never opened'}</span>}
                        <span className={'ajp-chip ' + (r.lastAudit?.verdict === 'issue' ? 'bad' : '')}>
                          {r.lastAudit ? `${r.lastAudit.verdict === 'ok' ? 'verified' : 'issue'} ${ago(r.lastAudit.at)}` : 'never audited'}
                        </span>
                        {paused && !r.unanswered && <span className="ajp-chip paused">replied by email</span>}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div>{renderDetail()}</div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
